import { Type, type Static } from "@sinclair/typebox";
import type { PromotionReward, PromotionRequirement } from "./promotions";

// ----------------------------
// Targeting Conditions (TypeBox — validated before DB storage)
// ----------------------------

const PeriodSchema = Type.Union([
  Type.Literal("30d"),
  Type.Literal("90d"),
  Type.Literal("365d"),
  Type.Literal("all_time"),
]);

const OperatorSchema = Type.Union([
  Type.Literal("gte"),
  Type.Literal("lte"),
]);

export const SpendingConditionSchema = Type.Object({
  type: Type.Literal("total_spending"),
  operator: OperatorSchema,
  value: Type.Number({ minimum: 0 }),
  period: PeriodSchema,
});
export type SpendingCondition = Static<typeof SpendingConditionSchema>;

export const InactivityConditionSchema = Type.Object({
  type: Type.Literal("last_order_days_ago"),
  operator: OperatorSchema,
  value: Type.Integer({ minimum: 1 }),
});
export type InactivityCondition = Static<typeof InactivityConditionSchema>;

export const BirthdayConditionSchema = Type.Object({
  type: Type.Literal("birthday"),
  window: Type.Union([
    Type.Literal("today"),
    Type.Literal("this_week"),
    Type.Literal("this_month"),
  ]),
});
export type BirthdayCondition = Static<typeof BirthdayConditionSchema>;

export const OrderedCategoryConditionSchema = Type.Object({
  type: Type.Literal("ordered_category"),
  category_ids: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
  period: PeriodSchema,
});
export type OrderedCategoryCondition = Static<
  typeof OrderedCategoryConditionSchema
>;

export const StatusLevelConditionSchema = Type.Object({
  type: Type.Literal("status_level"),
  value: Type.String({ minLength: 1 }),
});
export type StatusLevelCondition = Static<typeof StatusLevelConditionSchema>;

export const OrderCountConditionSchema = Type.Object({
  type: Type.Literal("order_count"),
  operator: OperatorSchema,
  value: Type.Integer({ minimum: 0 }),
  period: PeriodSchema,
});
export type OrderCountCondition = Static<typeof OrderCountConditionSchema>;

export const CustomerConditionSchema = Type.Union([
  SpendingConditionSchema,
  InactivityConditionSchema,
  BirthdayConditionSchema,
  OrderedCategoryConditionSchema,
  StatusLevelConditionSchema,
  OrderCountConditionSchema,
]);
export type CustomerCondition = Static<typeof CustomerConditionSchema>;

// Conditions within a group are ANDed; groups are ORed (DNF)
export const ConditionGroupSchema = Type.Object({
  conditions: Type.Array(CustomerConditionSchema, { minItems: 1 }),
});
export type ConditionGroup = Static<typeof ConditionGroupSchema>;

export const CampaignTargetingSchema = Type.Union([
  Type.Object({
    type: Type.Literal("explicit"),
    customer_ids: Type.Array(Type.String(), { minItems: 1 }),
  }),
  Type.Object({
    type: Type.Literal("conditions"),
    groups: Type.Array(ConditionGroupSchema, { minItems: 1 }),
  }),
]);
export type CampaignTargeting = Static<typeof CampaignTargetingSchema>;

// ----------------------------
// Campaign
// ----------------------------

export const CampaignMessageTemplateSchema = Type.Object({
  subject: Type.Optional(Type.String()),
  body: Type.String(),
});
export type CampaignMessageTemplate = Static<
  typeof CampaignMessageTemplateSchema
>;

export type CampaignChannel = "email" | "whatsapp" | "sms";

export type Campaign = {
  id: string;
  shop_id: number;
  name: string;
  promotion_id: string;
  targeting: CampaignTargeting;
  channels: CampaignChannel[];
  message_template: CampaignMessageTemplate;
  coupon_expires_in_days: number | null;
  status: "draft" | "active" | "archived";
  created_at: string;
  updated_at: string;
  // serializer-computed stats
  total_runs: number;
  last_run_at: string | null;
  lifetime_sent: number;
  lifetime_failed: number;
};

// ----------------------------
// Campaign Run
// ----------------------------

export type CampaignRunStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed";

export type CampaignRun = {
  id: string;
  campaign_id: string;
  status: CampaignRunStatus;
  cloudflare_workflow_id: string | null;
  total_targeted: number | null;
  total_sent: number;
  total_failed: number;
  triggered_by: "manual" | "scheduled";
  triggered_by_user_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  created_at: string;
};

// ----------------------------
// Campaign Coupon
// ----------------------------

export type CouponStatus = "pending" | "sent" | "redeemed" | "expired";

export type CampaignCoupon = {
  id: string;
  campaign_id: string;
  campaign_run_id: string;
  customer_id: string;
  code: string;
  status: CouponStatus;
  sent_at: string | null;
  redeemed_at: string | null;
  redeemed_order_id: string | null;
  expires_at: string | null;
  created_at: string;
};

// ----------------------------
// Coupon Redemption (cashier)
// ----------------------------

export type CouponRedemptionResult = {
  valid: boolean;
  coupon_id: string;
  promotion_id: string;
  reward: PromotionReward;
  requirements: PromotionRequirement[];
  customer_id: string;
  expires_at: string | null;
};
